/**
 * @component CollegeModal
 * @description
 * Modal reutilizable para crear o editar un colegio. Muestra un formulario con campos como nombre, descripción, email y teléfono.
 * Se conecta a la API de colegios para crear o actualizar registros dependiendo de la acción especificada.
 *
 * @param {Object} props - Props del componente
 * @param {number} props.college_id - ID del colegio a editar (0 si es nuevo)
 * @param {string} props.action - Acción actual ("Nuevo" o "Editar")
 * @param {Function} props.handleClose - Función para cerrar el modal externamente
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {  updateCollegeById, createCollege, fetchColleges } from 'api/colleges/colleges';
import { errorNotification, handleApiError, isEmailValid, successNotification } from 'utils';

function CollegeModal({ college_id, action, handleClose }) {
    const token = useSelector(state => state.authentication.user?.token);

    const [formCollege, setFormCollege] = useState({
        name: '',
        description: '',
        email: '',
        phone: ''
    });

    /**
     * useEffect que carga los datos del colegio si se está editando uno existente.
     */
    useEffect(() => {
        if (token && college_id > 0) {
            loadCollegeById(college_id);
        }
        // eslint-disable-next-line
    }, [token, college_id]);

    /**
     * Carga un colegio desde la API según su ID.
     * @param {number} id - ID del colegio
     */
    const loadCollegeById = async (id) => {
        try {
            const { data } = await fetchColleges({ id }, token);
            setFormCollege(data);
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Llama a la API para registrar un nuevo colegio.
     */
    const handleCreateCollege = async () => {
        try {
            const { data } = await createCollege(formCollege, token);
            successNotification(data.message);
            handleCloseModal();
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Llama a la API para actualizar un colegio existente.
     * @param {number} id - ID del colegio
     */
    const handleUpdateCollegeById = async (id) => {
        try {
            const { data } = await updateCollegeById(id, formCollege, token);
            successNotification(data.message);
            handleCloseModal();
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Decide si crear o actualizar colegio, y valida el formulario antes de enviar.
     * @param {Event} e - Evento del formulario
     */
    const handleActionCollege = (e) => {
        e.preventDefault();
        if (e.target.form.checkValidity()) {
            action === "Nuevo"
                ? handleCreateCollege()
                : handleUpdateCollegeById(college_id);
        } else {
            errorNotification("Faltan campos requeridos.");
        }
    };

    /**
     * Maneja cambios en los inputs del formulario.
     * @param {Event} event - Evento del input
     */
    const handleChangeFormCollege = (event) => {
        const { name, value } = event.target;
        setFormCollege(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    /**
     * Cierra el modal limpiando el estado local.
     */
    const handleCloseModal = () => {
        document.getElementById('collegeModal').classList.remove('show');
        document.querySelector('.modal-backdrop').remove();
        document.getElementById('collegeModal').style.display = 'none';
        cleanForm();
        handleClose();
    };

    /**
     * Limpia los valores del formulario.
     */
    const cleanForm = () => {
        setFormCollege({});
    };

    return (
        <div id="collegeModal" className="modal modal-cover fade" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{action} Colegio</h5>
                        <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                    </div>
                    <div className="modal-body">
                        <form id="formCollege" className="was-validated">
                            <div className="row row-space-12">
                                <div className="col-xl-6 mb-3">
                                    <label className="form-label">Nombre</label>
                                    <input className="form-control" placeholder="Nombre" id="name" name="name" type="text"
                                        value={formCollege.name || ''} onChange={handleChangeFormCollege} required pattern=".*\S+.*" />
                                    <div className="invalid-feedback">Campo requerido</div>
                                </div>
                                <div className="col-xl-6 mb-3">
                                    <label className="form-label">Descripción</label>
                                    <input className="form-control" placeholder="Descripción" id="description" name="description"
                                        value={formCollege.description || ''} onChange={handleChangeFormCollege} />
                                </div>
                                <div className="col-xl-6 mb-3">
                                    <label className="form-label">Email</label>
                                    <input className="form-control" placeholder="Email" id="email" name="email" type="email"
                                        value={formCollege.email || ''} onChange={handleChangeFormCollege} />
                                </div>
                                <div className="col-xl-6 mb-3">
                                    <label className="form-label">Teléfono</label>
                                    <input className="form-control" placeholder="Teléfono" id="phone" name="phone"
                                        value={formCollege.phone || ''} onChange={handleChangeFormCollege} />
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-default" onClick={handleCloseModal}>Cerrar</button>
                        <button type="button" className="btn btn-outline-theme" onClick={handleActionCollege} form="formCollege">{action}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CollegeModal;